import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Center,
  HStack,
  Icon,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import numeral from 'numeral'
import type { ReactElement } from 'react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BsArrowRight } from 'react-icons/bs'
import { Link as reactLink, useHistory } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { currencySymbol } from '../../../utils'
import type {
  UpcomingInvoiceErrorResponse,
  UpcomingInvoiceSuccessResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'

const UpcomingInvoice = (): ReactElement | null => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const [data, setData] = useState<UpcomingInvoiceSuccessResponse>()
  const [loading, setLoading] = useState<boolean>(true)
  const [isRender, setIsRender] = useState<boolean>(true)
  const [error, setError] = useState<string>('')

  const { response, request } = useFetch<UpcomingInvoiceSuccessResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const history = useHistory()

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const res = (await request.get(
        `/api/clients/${user.clientNumber}/invoice/upcoming`,
      )) as UpcomingInvoiceErrorResponse | UpcomingInvoiceSuccessResponse

      if (request.error) {
        if (response.status === 404) {
          setIsRender(false)
        } else {
          setError(
            t(
              'error_fetching_invoice_data',
              'Error in fetching upcoming invoice details. Try refreshing the page.',
            ),
          )
        }
        setLoading(false)
      } else {
        setData(res as UpcomingInvoiceSuccessResponse)
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const displayValue = useMemo(() => {
    if (data) {
      const symbol = currencySymbol(data.displayCurrency.toUpperCase())
      const formattedAmount = numeral(data.displayAmount).format('0,0.00')

      return (
        <Trans t={t} i18nKey="upcoming_invoice_of_amount">
          Upcoming invoice of {{ symbol }} {{ formattedAmount }}.
        </Trans>
      )
    }
  }, [data?.displayAmount, data?.displayCurrency])

  return loading ? (
    <Center height="100%">
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="appPaleGrey"
        color="appBlue"
        size="xl"
      />
    </Center>
  ) : !isRender ? null : error ? (
    <Alert status="error" mb="1em">
      <AlertIcon />
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : (
    <Box
      borderRadius="16px"
      backgroundColor={colors.appPaleGrey}
      cursor="pointer"
      onClick={() => {
        history.push('/invoices/upcoming')
      }}
      justifyContent="space-between"
    >
      <HStack
        width="full"
        spacing="1em"
        justifyContent="space-between"
        padding="1.5em"
      >
        <VStack spacing="0.5em" alignItems="start">
          <Text color="#1a202c" fontSize="1.25rem" fontWeight="semibold">
            {displayValue}
          </Text>

          <Trans t={t} i18nKey="link_to_upcoming_invoice_page">
            <Text color="#4a5568">
              The cost is calculated based on total consumption up to the last
              hour. Click
              <Link lineHeight={1.2} as={reactLink} to="/invoices/upcoming">
                here
              </Link>
              to view the breakdown.
            </Text>
          </Trans>
        </VStack>

        <Box padding={0} marginTop={'2em'}>
          <Icon
            boxSize={8}
            color="#1a202c"
            as={BsArrowRight}
            aria-label={t('clickable', 'Clickable')}
          />
        </Box>
      </HStack>
    </Box>
  )
}

export default UpcomingInvoice
